import { FC } from "react";
import {
  Grid,
  Paper,
  Typography,
  Button,
  Link,
  Checkbox,
  FormGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Radio,
  RadioGroup,
  Chip,
  Stack,
  Box,
  Select,
  MenuItem,
  OutlinedInput,
  InputLabel,
  InputAdornment,
  Alert,
  FormHelperText,
  Switch,
  Autocomplete,
  Tabs,
  Tab,
  Card,
  Divider,
  ListItemIcon,
  Snackbar,
  Tooltip,
  IconButton,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import SendIcon from "@mui/icons-material/Send";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";

import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import SearchIcon from "@mui/icons-material/Search";
import BookmarksOutlinedIcon from "@mui/icons-material/BookmarksOutlined";
import AddIcon from "@mui/icons-material/Add";

import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import RequiredIndicator from "../../../Components/Misc/RequiredIndicator";
import ToggleLayout from "../../../Components/Misc/ToggleLayout";
import { useToggle } from "../../../Utils/Hooks/useToggle";
import OutlinedCheckbox from "../../../Components/Forms/OutlinedCheckbox";
import FormSection from "../../../Components/Forms/FormSection/form-section";
import Tabbar from "../../../Components/Tabbar";
import { NavTypes, routePaths } from "../../../Router/router";
import MoreMenu from "../../../Components/Misc/MoreMenu";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import Cover from "../../../Components/Misc/Cover";
import PosterImage from "../../../Components/Misc/PosterImage";
import AssetThumbnail from "../../../Components/Misc/AssetThumbnail";
import Toast from "../../../Components/Misc/Toast/toast";
import PosterImagePack from "../../../Components/Misc/PosterImagePack";
import SearchExpand from "../../../Components/Misc/SearchExpand/search-expand";
import { useTheme } from "@emotion/react";
import { AssetTypeAlphaColor, OrderTypeAlphaColor } from "../../../Utils/helpers";
import AssetTypeChip from "../../../Components/Misc/Chips/AssetChip/asset-type-chip";
import AssetIcon from "../../../Components/Misc/AssetIcon/asset-icon";
import DemoFigmaTitle from "../../../Components/Demo/DemoFigmaTitle";
import UserChip from "../../../Components/Misc/Chips/UserChip/user-chip";
import UserGroupChip from "../../../Components/Misc/Chips/UserGroupChip/user-group-chip";
import { ObjectType } from "../../../Core/Api/Types/object-type";
import { OrderProductType } from "../../../Core/Api/Types/order-product-type";
import { ObjectLayer } from "../../../Core/Api/Types/object-layer";
import AssetLayerChip from "../../../Components/Misc/Chips/AssetChip/asset-layer-chip";
import { ColorType } from "../../../Core/Api/Types/color-type";

import PageHead from "../../../Components/Topbars/DashboardTopbar/page-head";
import { AddCircle } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

const Color: FC<{ color: string; option?: string; text?: string }> = ({ color, option, text }) => {
  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <Grid item md={2} alignItems="center">
      <Box
        width={40}
        height={40}
        borderRadius={0.5}
        sx={(theme) => ({
          border: `1px solid ${theme.palette.backgroundAlpha.border}`,
          // @ts-ignore
          background: option ? theme.palette[color][option] : color,
        })}
      />
      <Typography variant="h6" component="span">
        {text || `${color}.${option}`}
        <br />
        {/* @ts-ignore */}
        {option && theme.palette[color][option]}
      </Typography>
    </Grid>
  );
};

function DemoCore() {
  const { t } = useTranslation();
  const { active: outlinedCheckboxChecked, toggle: toggleOutlinedCheckbox } = useToggle(false);
  const { active: toasterVisible, toggle: toggleToaster, unactivate: closeToaster } = useToggle();

  return (
    <>
      <Paper sx={{ p: 2, mb: 2, backgroundImage: "none" }}>
        <h1 id="palette">{t("common:palette")}</h1>
        <h2 id="palette-primary">{t("common:primary")}</h2>
        <Grid container spacing={1} mb={6}>
          <Color color="primary" option="main" />
          <Color color="primary" option="mainHover" />
          <Color color="primary" option="mainPressed" />
          <Color color="primary" option="text" />
          <Color color="primary" option="alpha" />
          <Color color="primary" option="alphaHover" />
          <Color color="primary" option="alphaPressed" />
        </Grid>
        <h2 id="palette-surfaces">{t("page:body.surfaces")}</h2>
        <Grid container spacing={1} mb={6}>
          <Color color="background" option="default" text="surfaces.00 (background.default)" />
          <Color color="background" option="paper" text="surfaces.01 (background.paper)" />
          <Color color="background" option="200" text="surfaces.02 (background.200)" />
          <Color color="background" option="300" text="surfaces.03 (background.300)" />
          <Color color="background" option="400" text="surfaces.04 (background.400)" />
          <Color color="background" option="alphaHover" />
        </Grid>
        <h2 id="palette-surfaces-alpha">{t("page:body.surfacesAlpha")}</h2>
        <Grid container spacing={1} mb={6}>
          <Color color="backgroundAlpha" option="main" text="surfaces.main" />
          <Color color="backgroundAlpha" option="hover" text="surfaces.hover" />
          <Color color="backgroundAlpha" option="pressed" text="surfaces.pressed" />
          <Color color="backgroundAlpha" option="border" text="surfaces.border" />
          <Color color="backgroundAlpha" option="borderFocus" text="surfaces.borderFocus" />
        </Grid>
        <h2 id="palette-text">{t("common:text")}</h2>
        <Grid container spacing={1} mb={6}>
          <Color color="text" option="primary" />
          <Color color="text" option="secondary" />
          <Color color="text" option="tier" />
          <Color color="text" option="placeholder" />
          <Color color="text" option="disabled" />
        </Grid>
        <h2 id="palette-asset-types">{t("page:body.assetTypes")}</h2>
        <Grid container spacing={1} mb={6}>
          <Color
            color={AssetTypeAlphaColor(ObjectType.Video /*TODO : Was trailer => find new equivalent*/, 1)}
            text={t("common:trailer")}
          />
          <Color color={AssetTypeAlphaColor(ObjectType.Subtitle, 1)} text={t("field:placeholder.subtitle")} />
          <Color color={AssetTypeAlphaColor(ObjectType.Folder, 1)} text={t("common:folder")} />
          <Color color={AssetTypeAlphaColor(ObjectType.Audio, 1)} text={t("common:audio")} />
          <Color color={AssetTypeAlphaColor(ObjectType.Image, 1)} text={t("common:artwork")} />
          <Color
            color={OrderTypeAlphaColor(OrderProductType.TranscodeByProfile, 1)}
            text={t("page:color.text.selfBroadcasted")}
          />
          {/* TODO : Config not mapped <Color color={assetTypeAlphaColor("config", 1)} text="Config" /> */}
        </Grid>
      </Paper>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <PageHead />
        </Grid>
      </Grid>
      <Grid container spacing={3}>
        {/* Chart */}
        <Grid item xs={12} md={8} lg={9}>
          <Paper
            sx={{
              p: 2,
              display: "flex",
              flexDirection: "column",
              backgroundImage: "none",
            }}
          >
            <h1 id="typography">{t("page:body.typography")}</h1>
            <Typography variant="h1">{t("page:body.headingWithNumber", { number: 1 })}</Typography>
            <Typography variant="h2">{t("page:body.headingWithNumber", { number: 2 })}</Typography>
            <Typography variant="h3">{t("page:body.headingWithNumber", { number: 3 })}</Typography>
            <Typography variant="h4">{t("page:body.headingWithNumber", { number: 4 })}</Typography>
            <Typography variant="h5">{t("page:body.headingWithNumber", { number: 5 })}</Typography>
            <Typography variant="h6">{t("page:body.headingWithNumber", { number: 6 })}</Typography>
            <Typography variant="subtitle1">{t("page:body.subtitleWithNumber", { number: 1 })}</Typography>
            <Typography variant="subtitle2">{t("page:body.subtitleWithNumber", { number: 2 })}</Typography>
            <Typography variant="body1">
              {t("page:body.bodyWithNumber", { number: 1 })} / {t("common:default")}
            </Typography>
            <Typography variant="body2">{t("page:body.bodyWithNumber", { number: 2 })}</Typography>
            <Typography variant="button">{t("common:button")}</Typography>
          </Paper>
        </Grid>
        {/* Recent Deposits */}
        <Grid item xs={12} md={4} lg={3}>
          <Paper
            sx={{
              p: 2,
              display: "flex",
              flexDirection: "column",
              height: 240,
              backgroundImage: "none",
            }}
          >
            <p>{t("common:lowerCaseTest")}</p>
            <Button variant="contained" color="primary">
              {t("button:buttonTest")}
            </Button>
          </Paper>
        </Grid>
        {/* Recent Orders */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2, mb: 2, display: "flex", flexDirection: "column", backgroundImage: "none" }}>
            <h1 id="button">{t("common:button")}</h1>
            <Box sx={{ "& button": { m: 1 } }}>
              <Button size="small">{t("common:small")}</Button>
              <Button size="medium">{t("common:medium")}</Button>
              <Button size="large">{t("common:large")}</Button>
              <Button size="xlarge">{t("button:xLarge")}</Button>
              <Button size="large" disabled>
                {t("common:disabled")}
              </Button>
              <Button size="large" color="primary">
                {t("common:disabled")}
              </Button>
            </Box>
            <Box sx={{ "& button": { m: 1 } }}>
              <IconButton size="small" color="primary">
                <AddCircle />
              </IconButton>
              <IconButton size="medium" color="secondary">
                <AddCircle />
              </IconButton>
              <IconButton size="large" color="neutral">
                <AddCircle />
              </IconButton>
              <IconButton size="large" color="ghost">
                <AddCircle />
              </IconButton>
            </Box>
            <Box sx={{ "& button": { m: 1 } }}>
              <Button variant="outlined" size="small">
                {t("common:small")}
              </Button>
              <Button variant="outlined" size="medium">
                {t("common:medium")}
              </Button>
              <Button variant="outlined" size="large">
                {t("common:large")}
              </Button>
              <Button variant="outlined" size="xlarge">
                {t("button:xLarge")}
              </Button>
              <Button variant="outlined" size="large" disabled>
                {t("common:disabled")}
              </Button>
              <Button variant="outlined" startIcon={<DeleteIcon />}>
                {t("common:delete")}
              </Button>
            </Box>
            <Box sx={{ "& button": { m: 1 } }}>
              <Button variant="outlined" color="error" size="small">
                {t("common:small")}
              </Button>
              <Button variant="outlined" color="error" size="medium">
                {t("common:medium")}
              </Button>
              <Button variant="outlined" color="error" size="large">
                {t("common:large")}
              </Button>
              <Button variant="outlined" color="error" size="xlarge">
                {t("button:xLarge")}
              </Button>
              <Button variant="outlined" color="error" size="large" disabled>
                {t("common:disabled")}
              </Button>
              <Button variant="outlined" color="error">
                {t("common:delete")}
              </Button>
            </Box>
            <Box sx={{ "& button": { m: 1 } }}>
              <Button variant="contained" size="small">
                {t("common:small")}
              </Button>
              <Button variant="contained" size="medium">
                {t("common:medium")}
              </Button>
              <Button variant="contained" size="large">
                {t("common:large")}
              </Button>
              <Button variant="contained" size="xlarge">
                {t("button:xLarge")}
              </Button>
              <Button variant="contained" size="large" disabled>
                {t("common:disabled")}
              </Button>
            </Box>
            <Box sx={{ "& button": { m: 1 } }}>
              <Button variant="white" size="small">
                {t("common:small")}
              </Button>
              <Button variant="white" size="medium">
                {t("common:medium")}
              </Button>
              <Button variant="white" size="large">
                {t("common:large")}
              </Button>
              <Button variant="white" size="xlarge">
                {t("button:xLarge")}
              </Button>
              <Button variant="white" size="large" disabled>
                {t("common:disabled")}
              </Button>
            </Box>

            <Box sx={{ "& button": { m: 1 } }}>
              <Button variant="rounded" size="small">
                {t("common:small")}
              </Button>
              <Button variant="rounded" size="medium">
                {t("common:medium")}
              </Button>
              <Button variant="rounded" size="large">
                {t("common:large")}
              </Button>
              <Button variant="rounded" size="xlarge">
                {t("button:xLarge")}
              </Button>
              <Button variant="rounded" size="xlarge" disabled>
                {t("common:disabled")}
              </Button>
            </Box>
            <Box sx={{ "& button": { m: 1 } }}>
              <Button variant="contained" color="error" size="small">
                {t("common:small")}
              </Button>
              <Button variant="contained" color="error" size="medium">
                {t("common:medium")}
              </Button>
              <Button variant="contained" color="error" size="large">
                {t("common:large")}
              </Button>
              <Button variant="contained" color="error" size="xlarge">
                {t("button:xLarge")}
              </Button>
              <Button variant="contained" color="error" size="large" disabled>
                {t("common:disabled")}
              </Button>
            </Box>
            <Box sx={{ "& button": { m: 1 } }}>
              <Button variant="contained" size="small" endIcon={<SendIcon />}>
                {t("common:small")}
              </Button>
              <Button variant="contained" size="medium" endIcon={<SendIcon />}>
                {t("common:medium")}
              </Button>
              <Button variant="contained" size="large" endIcon={<SendIcon />}>
                {t("common:large")}
              </Button>
              <Button variant="contained" size="xlarge" endIcon={<SendIcon />}>
                {t("button:xLarge")}
              </Button>
            </Box>
            <Box sx={{ "& button": { m: 1 } }}>
              <Button variant="icon" size="small">
                <SendIcon />
              </Button>
              <Button variant="icon" size="medium">
                <SendIcon />
              </Button>
              <Button variant="icon" size="large">
                <SendIcon />
              </Button>
              <Button variant="icon" size="xlarge">
                <SendIcon />
              </Button>
            </Box>
            <div>
              <ToggleLayout value="grid" size="small" />
              <ToggleLayout value="grid" />
            </div>
            <div>
              <Link href="#" underline="hover" variant="subtitle1" color="primary.text">
                <ArrowBackRoundedIcon />
                {t("button:textButton")}
                <SendIcon />
              </Link>
              <Link href="#" underline="hover" variant="subtitle2" color="primary.text">
                {t("button:textButton")}
              </Link>
              <Link href="#" underline="hover" variant="body1" color="primary.text">
                {t("button:textButton")}
              </Link>
              <Link href="#" underline="hover" variant="body2" color="primary.text">
                {t("button:textButton")}
              </Link>
            </div>
            <div>
              <Link href="#" underline="hover" variant="subtitle1" color="text.secondary">
                <ArrowBackRoundedIcon sx={{ width: 16, height: 16, mr: 1 }} />
                {t("button:textButton")}
              </Link>
              <Link href="#" underline="hover" variant="subtitle2" color="text.secondary">
                {t("button:textButton")}
              </Link>
              <Link href="#" underline="hover" variant="body1" color="text.secondary">
                {t("button:textButton")}
              </Link>
              <Link href="#" underline="hover" variant="body2" color="text.secondary">
                {t("button:textButton")}
              </Link>
            </div>
          </Paper>
          <Paper sx={{ p: 2, display: "flex", backgroundImage: "none", flexDirection: "column", "& h1": { mt: 4 } }}>
            <Stack>
              <h1 id="input">{t("common:input")}</h1>

              <OutlinedInput
                label={t("common:standard")}
                sx={{ mb: 2 }}
                placeholder={t("common:placeholder")}
                endAdornment={
                  <InputAdornment position="end">
                    <SearchIcon />
                  </InputAdornment>
                }
              />

              <FormControl size="small" sx={{ mb: 2 }}>
                <InputLabel shrink htmlFor="bootstrap-input1">
                  {t("field:label.inputSmall")}
                </InputLabel>
                <OutlinedInput defaultValue="react-bootstrap" id="bootstrap-input1" startAdornment={<DeleteIcon />} />
              </FormControl>

              <FormControl sx={{ mb: 2 }}>
                <InputLabel shrink htmlFor="bootstrap-input1">
                  {t("common:input")}
                </InputLabel>
                <OutlinedInput defaultValue="react-bootstrap" id="bootstrap-input1" startAdornment={<DeleteIcon />} />
              </FormControl>

              <FormControl size="large" sx={{ mb: 2 }}>
                <InputLabel shrink htmlFor="bootstrap-input1">
                  {t("field:label.inputLarge")}
                </InputLabel>
                <OutlinedInput defaultValue="react-bootstrap" id="bootstrap-input1" startAdornment={<DeleteIcon />} />
              </FormControl>
              <FormControl size="xlarge" sx={{ mb: 2 }}>
                <InputLabel shrink htmlFor="bootstrap-input1">
                  {t("field:label.inputXLarge")}
                </InputLabel>
                <OutlinedInput defaultValue="react-bootstrap" id="bootstrap-input1" startAdornment={<DeleteIcon />} />
              </FormControl>

              <FormControl sx={{ mb: 2 }} error>
                <InputLabel shrink htmlFor="bootstrap-input1">
                  {t("common:input")}
                </InputLabel>
                <OutlinedInput defaultValue="react-bootstrap" id="bootstrap-input1" startAdornment={<DeleteIcon />} />
                <FormHelperText>{t("field:helper.errorMessage")}</FormHelperText>
              </FormControl>
            </Stack>

            <Stack>
              <h1 id="search-expand">{t("page:body.searchExpand")}</h1>
              <Box width="100%">
                <SearchExpand sx={{ width: "100%" }} />
              </Box>
            </Stack>

            <Stack>
              <h1 id="select">{t("common:select")}</h1>
              <Select
                displayEmpty
                IconComponent={ExpandMoreRoundedIcon}
                value=""
                inputProps={{ "aria-label": "Without label" }}
                size="small"
                sx={{ mb: 1 }}
              >
                <MenuItem value="">{t("menu:item.sortBy")}</MenuItem>
                <MenuItem value="title">{t("common:title")}</MenuItem>
                <MenuItem value="quality">{t("menu:item.bestQuality")}</MenuItem>
              </Select>
              <Select
                displayEmpty
                IconComponent={ExpandMoreRoundedIcon}
                value=""
                inputProps={{ "aria-label": "Without label" }}
                sx={{ mb: 1 }}
              >
                <MenuItem value="">{t("menu:item.sortBy")}</MenuItem>
                <MenuItem value="title">{t("common:title")}</MenuItem>
                <MenuItem value="quality">{t("menu:item.bestQuality")}</MenuItem>
              </Select>
              <Select
                displayEmpty
                IconComponent={ExpandMoreRoundedIcon}
                value=""
                inputProps={{ "aria-label": "Without label" }}
                size="large"
                sx={{ mb: 1 }}
              >
                <MenuItem value="">{t("menu:item.sortBy")}</MenuItem>
                <MenuItem value="title">{t("common:title")}</MenuItem>
                <MenuItem value="quality">{t("menu:item.bestQuality")}</MenuItem>
              </Select>
              <Select
                displayEmpty
                IconComponent={ExpandMoreRoundedIcon}
                value=""
                inputProps={{ "aria-label": "Without label" }}
                size="xlarge"
                sx={{ mb: 1 }}
              >
                <MenuItem value="">{t("menu:item.sortBy")}</MenuItem>
                <MenuItem value="title">{t("common:title")}</MenuItem>
                <MenuItem value="quality">{t("menu:item.bestQuality")}</MenuItem>
              </Select>
            </Stack>
            <Stack>
              <h1 id="autocomplete">{t("page:body.autocomplete")}</h1>
              <Autocomplete
                disablePortal
                id="autocomplete-small"
                size="small"
                options={[
                  { label: "The Shawshank Redemption", year: 1994 },
                  { label: "The Godfather", year: 1972 },
                  { label: "The Godfather: Part II", year: 1974 },
                ]}
                multiple
                sx={{ flex: 1, mb: 1 }}
                popupIcon={<ExpandMoreRoundedIcon />}
                renderInput={(params) => (
                  <OutlinedInput
                    {...params.InputProps}
                    inputProps={params.inputProps}
                    placeholder={t("common:small")}
                    sx={{ width: "100%" }}
                  />
                )}
              />
              <Autocomplete
                disablePortal
                id="autocomplete-small"
                size="medium"
                options={[
                  { label: "The Shawshank Redemption", year: 1994 },
                  { label: "The Godfather", year: 1972 },
                  { label: "The Godfather: Part II", year: 1974 },
                ]}
                multiple
                sx={{ flex: 1, mb: 1 }}
                popupIcon={<ExpandMoreRoundedIcon />}
                renderInput={(params) => (
                  <OutlinedInput
                    {...params.InputProps}
                    inputProps={params.inputProps}
                    placeholder={t("common:medium")}
                    sx={{ width: "100%" }}
                  />
                )}
              />
              <Autocomplete
                disablePortal
                id="autocomplete-small"
                size="large"
                options={[
                  { label: "The Shawshank Redemption", year: 1994 },
                  { label: "The Godfather", year: 1972 },
                  { label: "The Godfather: Part II", year: 1974 },
                ]}
                multiple
                sx={{ flex: 1, mb: 1 }}
                popupIcon={<ExpandMoreRoundedIcon />}
                renderInput={(params) => (
                  <OutlinedInput
                    {...params.InputProps}
                    inputProps={params.inputProps}
                    placeholder={t("common:large")}
                    sx={{ width: "100%" }}
                  />
                )}
              />
              <Autocomplete
                disablePortal
                id="autocomplete-small"
                size="xlarge"
                options={[
                  { label: "The Shawshank Redemption", year: 1994 },
                  { label: "The Godfather", year: 1972 },
                  { label: "The Godfather: Part II", year: 1974 },
                ]}
                multiple
                sx={{ flex: 1, mb: 1 }}
                popupIcon={<ExpandMoreRoundedIcon />}
                renderInput={(params) => (
                  <OutlinedInput
                    {...params.InputProps}
                    inputProps={params.inputProps}
                    placeholder={t("field:placeholder.xLarge")}
                    sx={{ width: "100%" }}
                  />
                )}
              />
            </Stack>
            <Box>
              <h1 id="checkbox">{t("page:body.checkbox")}</h1>
              <FormGroup>
                <FormControlLabel control={<Checkbox defaultChecked />} label={t("common:labelUpperCase")} />
                <FormControlLabel control={<Checkbox />} label={t("page:body.labelWithNumber", { number: 2 })} />
                <FormControlLabel disabled control={<Checkbox />} label={t("common:disabled")} />
              </FormGroup>
              <OutlinedCheckbox checked={outlinedCheckboxChecked} onClick={toggleOutlinedCheckbox}>
                <Typography fontWeight={500}>{t("page:body.outlinedCheckbox")}</Typography>
                <Typography fontWeight={500} variant="body2" color="text.tier">
                  {t("common:content")}
                </Typography>
              </OutlinedCheckbox>
              <OutlinedCheckbox
                direction="row-reverse"
                checked={outlinedCheckboxChecked}
                onClick={toggleOutlinedCheckbox}
              >
                <Typography fontWeight={500}>{t("page:body.outlinedCheckbox")}</Typography>
                <Typography fontWeight={500} variant="body2" color="text.tier">
                  {t("common:content")}
                </Typography>
              </OutlinedCheckbox>
            </Box>
            <Box>
              <h1 id="radio">{t("page:body.radio")}</h1>
              <FormControl>
                <FormLabel id="demo-radio-buttons-group-label">{t("common:gender")}</FormLabel>
                <RadioGroup
                  aria-labelledby="demo-radio-buttons-group-label"
                  defaultValue="female"
                  name="radio-buttons-group"
                >
                  <FormControlLabel value="female" control={<Radio />} label={t("common:female")} />
                  <FormControlLabel value="male" control={<Radio />} label={t("common:male")} />
                  <FormControlLabel value="other" control={<Radio />} label={t("common:other")} />
                </RadioGroup>
              </FormControl>
              <Box>
                <h1 id="switch">{t("common:switch")}</h1>
                <FormGroup>
                  <FormControlLabel control={<Switch defaultChecked />} label={t("common:labelUpperCase")} />
                  <FormControlLabel control={<Switch />} label={t("common:labelUpperCase")} />
                  <FormControlLabel disabled control={<Switch />} label={t("common:disabled")} />
                </FormGroup>
              </Box>
            </Box>

            <Stack>
              <h1 id="textarea">{t("common:textarea")}</h1>
              <FormControl sx={{ mb: 2 }}>
                <InputLabel shrink htmlFor="bootstrap-input1">
                  {t("common:textarea")}
                </InputLabel>
                <OutlinedInput
                  defaultValue="Est ex amet voluptate nostrud magna et occaecat anim elit in mollit laboris elit aliqua."
                  id="bootstrap-input1"
                  multiline
                />
              </FormControl>
              <FormControl size="large" sx={{ mb: 2 }}>
                <InputLabel shrink htmlFor="bootstrap-input1">
                  {t("field:label.textareaLarge")}
                </InputLabel>
                <OutlinedInput
                  defaultValue="Est ex amet voluptate nostrud magna et occaecat anim elit in mollit laboris elit aliqua."
                  id="bootstrap-input1"
                  multiline
                />
              </FormControl>
              <FormControl size="xlarge" sx={{ mb: 2 }}>
                <InputLabel shrink htmlFor="bootstrap-input1">
                  {t("field:label.textareaXLarge")}
                </InputLabel>
                <OutlinedInput
                  defaultValue="Est ex amet voluptate nostrud magna et occaecat anim elit in mollit laboris elit aliqua."
                  id="bootstrap-input1"
                  multiline
                />
              </FormControl>
            </Stack>
            <Box>
              <h1 id="date-picker">{t("page:body.datePicker")}</h1>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                {/* <DatePicker
                  value="2022-08-18T21:11:54"
                  format="dd/MM/yyyy"
                  onChange={() => null}
                  renderInput={(params) => (
                    <OutlinedInput
                      ref={params.inputRef}
                      {...params.InputProps}
                      inputProps={params.inputProps}
                      sx={{ width: "100%" }}
                    />
                  )}
                /> */}
              </LocalizationProvider>
            </Box>

            <Box>
              <h1 id="tag-chip">
                {t("common:tag")} / {t("common:chip")}
              </h1>
              <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                <Chip label={t("chip:label.deletable")} variant="tag" size="micro" onDelete={() => false} />
                <Chip label={t("chip:label.deletable")} variant="tag" size="small" onDelete={() => false} />
                <Chip label={t("chip:label.deletable")} variant="tag" size="medium" onDelete={() => false} />
                <Chip label={t("chip:label.deletable")} variant="tag" size="large" onDelete={() => false} />
                <Chip label={t("chip:label.deletable")} variant="tag" size="large" onDelete={() => false} />
                <Chip label={t("chip:label.deletable")} variant="tag" size="large" onDelete={() => false} />
                <Chip label={t("chip:label.deletable")} variant="tag" size="large" onDelete={() => false} />
                <Chip label={t("chip:label.deletable")} variant="tag" size="large" onDelete={() => false} />
                <Chip label={t("chip:label.deletable")} variant="tag" size="large" onDelete={() => false} />
                <Chip label={t("chip:label.deletable")} variant="tag" size="large" onDelete={() => false} />
                <Chip label={t("chip:label.deletable")} variant="tag" size="large" onDelete={() => false} />
                <Chip label={t("chip:label.deletable")} variant="tag" size="large" onDelete={() => false} />
                <Chip label={t("chip:label.deletable")} variant="tag" size="large" onDelete={() => false} />
              </Stack>
              <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                <Chip label={t("common:lowerCaseDefault")} color="default" />
                <Chip label={t("common:lowerCasePrimary")} color="primary" />
                <Chip label={t("common:error")} color="error" />
                <Chip label={t("common:info")} color="info" />
                <Chip label={t("common:lowerCaseSuccess")} color="success" />
                <Chip label={t("common:lowerCaseWarning")} color="warning" />
              </Stack>
              <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                <Chip label={t("common:lowerCaseDefault")} color="default" clickable={true} />
                <Chip label={t("common:lowerCasePrimary")} color="primary" clickable={true} />
                <Chip label={t("common:error")} color="error" clickable={true} />
                <Chip label={t("common:info")} color="info" clickable={true} />
                <Chip label={t("common:lowerCaseSuccess")} color="success" clickable={true} />
                <Chip label={t("common:lowerCaseWarning")} color="warning" clickable={true} />
              </Stack>
              <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                <Chip label={t("common:lowerCaseDefault")} color="default" variant="outlined" />
                <Chip label={t("common:lowerCasePrimary")} color="primary" variant="outlined" />
                <Chip label={t("common:error")} color="error" variant="outlined" />
                <Chip label={t("common:info")} color="info" variant="outlined" />
                <Chip label={t("common:lowerCaseSuccess")} color="success" variant="outlined" />
                <Chip label={t("common:lowerCaseWarning")} color="warning" variant="outlined" />
              </Stack>
              <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                <Chip
                  label={t("chip:label.micro")}
                  color="primary"
                  variant="outlined"
                  size="micro"
                  icon={<AddIcon />}
                />
                <Chip label={t("common:small")} color="primary" variant="outlined" size="small" icon={<AddIcon />} />
                <Chip label={t("common:medium")} color="primary" variant="outlined" icon={<AddIcon />} />
                <Chip label={t("common:large")} color="primary" variant="outlined" size="large" icon={<AddIcon />} />
              </Stack>
              <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                <Chip label={t("chip:label.micro")} size="micro" />
                <Chip label={t("common:small")} size="small" />
                <Chip label={t("common:medium")} />
                <Chip label={t("common:large")} size="large" />
              </Stack>

              <h2 id="user-chip">{t("page:body.userChip")}</h2>

              <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                <UserChip colorType={"green"} />
                <UserChip colorType={"yellow"} />
                <UserChip colorType={"red"} />
                <UserChip colorType={"orange"} />
                <UserChip colorType={null} />
              </Stack>

              <h2 id="user-chip-name">{t("page:body.userChipName")}</h2>
              <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                {/*<UserGroupChip colorType={"green"} name="Name" />
                <UserGroupChip colorType={"yellow"} name="Name" />
                <UserGroupChip colorType={"red"} name="Name" />
                <UserGroupChip colorType={"orange"} name="Name" />
                  <UserGroupChip colorType={null} name="Name" />*/}
              </Stack>
              <h2 id="asset-chip">{t("page:body.assetChipType")}</h2>
              <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                <AssetTypeChip type={ObjectType.Video} />
                <AssetTypeChip type={ObjectType.Subtitle} />
                <AssetTypeChip type={ObjectType.Audio} />
              </Stack>
              <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                <AssetTypeChip type={ObjectType.Video} uncolored />
                <AssetTypeChip type={ObjectType.Subtitle} uncolored />
                <AssetTypeChip type={ObjectType.Audio} uncolored />
              </Stack>

              <h2 id="asset-chip">{t("page:body.assetChipLayer")}</h2>
              <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                <AssetLayerChip layer={ObjectLayer.Title} />
                <AssetLayerChip layer={ObjectLayer.Asset} />
                <AssetLayerChip layer={ObjectLayer.Content} />
              </Stack>
              <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                <AssetLayerChip layer={ObjectLayer.Title} uncolored />
                <AssetLayerChip layer={ObjectLayer.Asset} uncolored />
                <AssetLayerChip layer={ObjectLayer.Content} uncolored />
              </Stack>
            </Box>
            <Box>
              <h1 id="icon">{t("page:body.icons")}</h1>
              <Stack direction="row" spacing={1} mb={2}>
                <DeleteIcon fontSize="large" />
                <DeleteIcon fontSize="medium" />
                <DeleteIcon fontSize="small" />
              </Stack>
            </Box>
            <Box>
              <h1 id="tabbar">{t("page:body.tabbar")}</h1>
              <h2 id="tabbar-links">{t("page:body.linkTabs")}</h2>
              <Tabbar
                tabs={[
                  [
                    {
                      to: routePaths.demo.path,
                      label: t("page:tab.label.demoPage"),
                      matchingPaths: [routePaths.demo.path],
                    },
                    {
                      to: routePaths.catalog.path,
                      label: (
                        <>
                          {t("page:tab.label.homepage")} <RequiredIndicator sx={{ ml: 0.5 }} />
                        </>
                      ),
                    },
                  ],
                ]}
                sx={{ mb: 2 }}
              />
            </Box>
            <Box>
              <h2 id="tabbar-simple">{t("page:body.simpleTabs")}</h2>
              <Tabs value={0} onChange={() => {}} aria-label="basic tabs example">
                <Tab label={t("page:body.itemWithLetter", { letter: "A" })} />
                <Tab
                  label={
                    <>
                      {t("page:body.itemWithLetter", { letter: "B" })} <RequiredIndicator sx={{ ml: 0.5 }} />
                    </>
                  }
                />
                <Tab label={t("page:body.itemWithLetter", { letter: "C" })} />
              </Tabs>
            </Box>
            <Box>
              <h1 id="action-menu">{t("page:body.actionMenus")}</h1>
              <Stack alignItems="flex-start">
                <h2 id="action-menu-more">{t("page:body.moreMenu")}</h2>
                <MoreMenu>
                  <MenuItem>
                    <ListItemIcon>
                      <BookmarksOutlinedIcon fontSize="small" />
                    </ListItemIcon>
                    {t("page:body.actionWithNumber", { number: 1 })}
                  </MenuItem>
                  <MenuItem sx={{ color: "error.main" }}>
                    <ListItemIcon>
                      <DeleteOutlinedIcon fontSize="small" sx={{ color: "error.main" }} />
                    </ListItemIcon>
                    {t("page:body.actionWithNumber", { number: 2 })}
                  </MenuItem>
                </MoreMenu>
              </Stack>
            </Box>
          </Paper>

          <Paper sx={{ p: 2, mt: 4, display: "flex", flexDirection: "column", backgroundImage: "none" }}>
            <h1 id="form-sections">{t("page:body.formSections")}</h1>
            <Stack spacing={1}>
              <FormSection
                icon={<SendIcon />}
                title={`${t("form:section.title.formSection")} - ${t("common:status")} : ${t("common:todo")}`}
                status="todo"
                helperText={t("form:helper.helperText")}
              >
                <FormControlLabel
                  control={<Switch />}
                  labelPlacement="start"
                  sx={{ ml: 0, justifyContent: "flex-end", mb: 2 }}
                  label={t("common:switch")}
                />
                <Card variant="outlined" sx={{ p: 2, borderRadius: 0.5 }}>
                  <Stack spacing={2} direction="row">
                    <Stack flex={1}>
                      <FormControl>
                        <InputLabel shrink htmlFor="bootstrap-input1">
                          {t("field:label.inputText")}
                        </InputLabel>
                        <OutlinedInput />
                      </FormControl>
                    </Stack>
                    <Stack flex={1}>
                      <FormControl>
                        <InputLabel shrink htmlFor="bootstrap-input1">
                          {t("field:label.inputText")}
                        </InputLabel>
                        <OutlinedInput />
                      </FormControl>
                    </Stack>
                  </Stack>
                </Card>
              </FormSection>
              <FormSection
                size="small"
                icon={<SendIcon />}
                title={`${t("form:section.title.formSection")} - ${t("common:status")} : ${t("common:todo")} - ${t(
                  "common:size"
                )}: ${t("common:lowerCaseSmall")}`}
                status="todo"
              />
              <FormSection
                icon={<SendIcon />}
                title={`${t("form:section.title.formSection")} - ${t("common:status")} : ${t("common:todo")}`}
                status="todo"
                statusText={t("form:statusText.required")}
                helperText={t("form:helper.helperText")}
              />
              <FormSection
                icon={<SendIcon />}
                title={`${t("form:section.title.formTitle")} - ${t("common:status")} : ${t("common:warning")}`}
                status="warning"
                statusText={t("form:statusText.informationText")}
                helperText={t("form:helper.helperText")}
              />
              <FormSection
                icon={<SendIcon />}
                title={`${t("form:section.title.formTitle")} - ${t("common:status")} : ${t("common:upperCaseError")}`}
                status="error"
                statusText={t("form:statusText.statusTextUpperCase")}
                helperText={t("form:helper.helperText")}
              />
              <FormSection
                icon={<SendIcon />}
                title={`${t("form:section.title.formTitle")} - ${t("common:status")} : ${t("common:complete")}`}
                statusText={t("form:statusText.statusText")}
                status="complete"
                helperText={t("form:helper.helperText")}
              />
            </Stack>
          </Paper>

          <Paper sx={{ p: 2, mt: 4, display: "flex", flexDirection: "column", backgroundImage: "none" }}>
            <h1 id="cards">{t("common:cards")}</h1>
            <Stack direction="row" spacing={2}>
              <Card variant="outlined" sx={{ width: 200, height: 200, p: 2 }}>
                <Typography variant="h3">{t("page:body.cardOutlined")}</Typography>
                <Typography color="text.tier">{t("page:body.cardContent")}</Typography>
              </Card>
              <Card variant="outlined" sx={{ width: 200, height: 200, p: 2 }} aria-selected="true">
                <Typography variant="h3">{t("page:body.cardOutlined")}</Typography>
                <Typography color="text.tier">{t("page:body.cardSelected")}</Typography>
              </Card>
              <Card sx={{ width: 200, height: 200, p: 2 }}>
                <Typography variant="h3">{t("common:card")}</Typography>
                <Typography color="text.tier">{t("page:body.cardContent")}</Typography>
              </Card>
            </Stack>
          </Paper>

          <Paper sx={{ p: 2, mt: 4, display: "flex", flexDirection: "column", backgroundImage: "none" }}>
            <h1 id="alert">{t("page:body.alert")}</h1>
            <Alert severity="error">
              {t("alert:error.thisIsAnErrorAlert")} — {t("common:checkItOut")}
            </Alert>
            <Alert severity="warning">
              {t("alert:warning.thisIsAWarningAlert")} — {t("common:checkItOut")}
            </Alert>
            <Alert severity="info">
              {t("alert:info.thisIsAnInfoAlert")} — {t("common:checkItOut")}
            </Alert>
            <Alert severity="success">{t("alert:success.titlesWereAdded")}</Alert>
          </Paper>

          <Paper sx={{ p: 2, mt: 4, display: "flex", flexDirection: "column", backgroundImage: "none" }}>
            <h1 id="tooltip">{t("page:body.tooltip")}</h1>
            <Stack alignItems="flex-start" spacing={2}>
              <Tooltip
                placement="left"
                arrow
                title={
                  <>
                    <Typography fontWeight={500}>Titre tooltip</Typography>
                    <Typography variant="body2">Tooltip avec desc</Typography>
                  </>
                }
              >
                <Stack alignItems="center" direction="row" spacing={0.5}>
                  <ErrorOutlineIcon /> <span>{t("page:body.openTooltipLeft")}</span>
                </Stack>
              </Tooltip>
              <Tooltip
                placement="right"
                arrow
                title={
                  <>
                    <Typography fontWeight={500}>Titre tooltip</Typography>
                    <Typography variant="body2">Tooltip avec desc</Typography>
                  </>
                }
              >
                <Stack alignItems="center" direction="row" spacing={0.5}>
                  <ErrorOutlineIcon /> <span>{t("page:body.openTooltipRight")}</span>
                </Stack>
              </Tooltip>

              <Tooltip
                arrow
                title={
                  <>
                    <Typography fontWeight={500}>Titre tooltip</Typography>
                    <Typography variant="body2">Tooltip avec desc</Typography>
                  </>
                }
              >
                <Stack alignItems="center" direction="row" spacing={0.5}>
                  <ErrorOutlineIcon /> <span>{t("page:body.openTooltipBottom")}</span>
                </Stack>
              </Tooltip>
              <Tooltip
                placement="top"
                arrow
                title={
                  <>
                    <Typography fontWeight={500}>Titre tooltip</Typography>
                    <Typography variant="body2">Tooltip avec desc</Typography>
                  </>
                }
              >
                <Stack alignItems="center" direction="row" spacing={0.5}>
                  <ErrorOutlineIcon /> <span>{t("page:body.openTooltipTop")}</span>
                </Stack>
              </Tooltip>
            </Stack>
          </Paper>

          <Paper sx={{ p: 2, mt: 4, display: "flex", flexDirection: "column" }}>
            <h1 id="toaster">{t("page:body.toaster")}</h1>
            <Snackbar open={toasterVisible} anchorOrigin={{ horizontal: "right", vertical: "bottom" }}>
              <Toast type="success" onClose={closeToaster} />
            </Snackbar>

            <Stack spacing={2} alignItems="flex-start">
              <Button onClick={toggleToaster}>
                {toasterVisible ? t("button:closeSnackbar") : t("button:openSnackbar")}
              </Button>
              <Toast title={t("common:lowerCaseSuccess")} type="success" />
              <Toast type="error" />
              <Toast type="warning" icon={<WarningAmberOutlinedIcon />} />
              <Toast type="primary" />
              <Toast
                rightContent={
                  <Link component="button" underline="hover" color="primary.text">
                    {t("common:action")}
                  </Link>
                }
              />
            </Stack>
          </Paper>

          <Paper sx={{ p: 2, mt: 4, display: "flex", flexDirection: "column", backgroundImage: "none" }}>
            <h1 id="images">{t("page:body.images")}</h1>
            <Stack direction="row" spacing={2}>
              <Stack>
                <h2 id="images-cover">{t("page:collectionCard.title.cover")}</h2>
                <Box width={100} height={100} borderRadius={1} position="relative" overflow="hidden">
                  <Cover img={{ src: "/static/images/temp/content-subtitle.png" }} />
                </Box>
              </Stack>
              <Stack>
                <h2 id="images-poster">{t("menu:item.poster")}</h2>
                <PosterImage img={{ src: "/static/images/temp/movie-cover-1.jpg" }} height={100} width={78} />
              </Stack>
              <Stack>
                <h2 id="images-poster-pack">{t("page:body.posterPack")}</h2>
                <PosterImagePack
                  imgs={[
                    {
                      src: "/static/images/temp/movie-cover-1.jpg",
                    },
                    {
                      src: "/static/images/temp/movie-cover-2.jpg",
                    },
                    {
                      src: "/static/images/temp/movie-cover-3.jpg",
                    },
                  ]}
                  posterProps={{
                    height: 52,
                    width: 40,
                    borderRadius: "2px",
                  }}
                />
              </Stack>
            </Stack>
            <Divider sx={{ mt: 3 }} />
            <h2 id="images-assets-thumbnails">
              {t("page:body.assetsThumbnails")}{" "}
              <DemoFigmaTitle>{`${t("common:card")}/${t("common:assets")}/${t("common:typology")}`}</DemoFigmaTitle>
            </h2>

            <Grid container>
              <Grid item md={6}>
                <h3>{t("common:trailer")}</h3>
                <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                  <AssetThumbnail
                    size="x-large"
                    type={ObjectType.Video /*TODO : Was trailer => find new equivalent*/}
                  />
                  <AssetThumbnail size="large" type={ObjectType.Video /*TODO : Was trailer => find new equivalent*/} />
                  <AssetThumbnail type={ObjectType.Video /*TODO : Was trailer => find new equivalent*/} />
                  <AssetThumbnail size="small" type={ObjectType.Video /*TODO : Was trailer => find new equivalent*/} />
                  <AssetThumbnail size="micro" type={ObjectType.Video /*TODO : Was trailer => find new equivalent*/} />
                </Stack>
              </Grid>
              <Grid item md={6}>
                <h3>{t("common:audio")}</h3>
                <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                  <AssetThumbnail size="x-large" type={ObjectType.Audio} />
                  <AssetThumbnail size="large" type={ObjectType.Audio} />
                  <AssetThumbnail type={ObjectType.Audio} />
                  <AssetThumbnail size="small" type={ObjectType.Audio} />
                  <AssetThumbnail size="micro" type={ObjectType.Audio} />
                </Stack>
              </Grid>
              <Grid item md={6}>
                <h3>Access</h3>
                <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                  <AssetThumbnail size="x-large" type={ObjectType.VirtualHead} />
                  <AssetThumbnail size="large" type={ObjectType.VirtualHead} />
                  <AssetThumbnail type={ObjectType.VirtualHead} />
                  <AssetThumbnail size="small" type={ObjectType.VirtualHead} />
                  <AssetThumbnail size="micro" type={ObjectType.VirtualHead} />
                </Stack>
              </Grid>
              <Grid item md={6}>
                <h3>{t("common:config")}</h3>
                <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                  {/* TODO : Change config 
                   <AssetThumbnail size="x-large" type="config" />
                  <AssetThumbnail size="large" type="config" />
                  <AssetThumbnail type="config" />
                  <AssetThumbnail size="small" type="config" />
                  <AssetThumbnail size="micro" type="config" /> */}
                </Stack>
              </Grid>
              <Grid item md={6}>
                <h3>{t("common:folder")}</h3>
                <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                  <AssetThumbnail size="x-large" type={ObjectType.Folder} />
                  <AssetThumbnail size="large" type={ObjectType.Folder} />
                  <AssetThumbnail type={ObjectType.Folder} />
                  <AssetThumbnail size="small" type={ObjectType.Folder} />
                  <AssetThumbnail size="micro" type={ObjectType.Folder} />
                </Stack>
              </Grid>
              <Grid item md={6}>
                <h3>{t("common:artwork")}</h3>
                <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                  <AssetThumbnail size="x-large" type={ObjectType.Image} />
                  <AssetThumbnail size="large" type={ObjectType.Image} />
                  <AssetThumbnail type={ObjectType.Image} />
                  <AssetThumbnail size="small" type={ObjectType.Image} />
                  <AssetThumbnail size="micro" type={ObjectType.Image} />
                </Stack>
              </Grid>
              <Grid item md={6}>
                <h3>{t("field:placeholder.subtitle")}</h3>
                <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                  <AssetThumbnail size="x-large" type={ObjectType.Subtitle} />
                  <AssetThumbnail size="large" type={ObjectType.Subtitle} />
                  <AssetThumbnail type={ObjectType.Subtitle} />
                  <AssetThumbnail size="small" type={ObjectType.Subtitle} />
                  <AssetThumbnail size="micro" type={ObjectType.Subtitle} />
                </Stack>
              </Grid>
            </Grid>
          </Paper>
          <Paper sx={{ p: 2, mt: 4, display: "flex", flexDirection: "column", backgroundImage: "none" }}>
            <h1>{t("page:body.assetIcon")}</h1>
            <Grid container spacing={3}>
              <Grid item md={1}>
                <AssetIcon type={ObjectType.Audio} />
                <Typography variant="h6">{t("common:lowerCaseAudio")}</Typography>
              </Grid>
              <Grid item md={1}>
                <AssetIcon type={ObjectType.Video /*TODO : Was trailer => find new equivalent*/} />
                <Typography variant="h6">{t("common:lowerCaseTrailer")}</Typography>
              </Grid>
              <Grid item md={1}>
                <AssetIcon type={ObjectType.Subtitle} />
                <Typography variant="h6">{t("common:subtitle")}</Typography>
              </Grid>
              <Grid item md={1}>
                {/* TODO : Change config 
                 <AssetIcon type="config"  /> */}
                <Typography variant="h6">{t("common:lowerCaseConfig")}</Typography>
              </Grid>
              <Grid item md={1}>
                <AssetIcon type={ObjectType.Folder} />
                <Typography variant="h6">{t("common:lowerCaseFolder")}</Typography>
              </Grid>
              <Grid item md={1}>
                <AssetIcon type={ObjectType.Image} />
                <Typography variant="h6">{t("common:lowerCaseArtwork")}</Typography>
              </Grid>
              {/* TODO : Set Order icons instead of asset icons with order types
               <Grid item md={1}>
                <AssetIcon type="video" />
                <Typography variant="h6">video</Typography>
              </Grid>
              <Grid item md={1}>
                <AssetIcon type={ConfigKeys.import} />
                <Typography variant="h6">{ConfigKeys.import}</Typography>
              </Grid>
              <Grid item md={1}>
                <AssetIcon type={ConfigKeys.VODPackage} />
                <Typography variant="h6">{ConfigKeys.VODPackage}</Typography>
              </Grid>
              <Grid item md={1}>
                <AssetIcon type={ConfigKeys.selfBroadcasted} />
                <Typography variant="h6">{ConfigKeys.selfBroadcasted}</Typography>
              </Grid>
              <Grid item md={1}>
                <AssetIcon type={ConfigKeys.transcode} />
                <Typography variant="h6">{ConfigKeys.transcode}</Typography>
              </Grid>
              <Grid item md={1}>
                <AssetIcon type={ConfigKeys.packageBundle} />
                <Typography variant="h6">{ConfigKeys.packageBundle}</Typography>
              </Grid>
              <Grid item md={1}>
                <AssetIcon type={ConfigKeys.theatricalMaterial} />
                <Typography variant="h6">{ConfigKeys.theatricalMaterial}</Typography>
              </Grid>
              <Grid item md={1}>
                <AssetIcon type="" />
                <Typography variant="h6">Autre</Typography>
              </Grid> */}
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </>
  );
}

export default DemoCore;
